import React, { useState } from "react";
import { CheckCircle2, Sparkles, Phone } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { BRAND } from "../data/content";

const fmtPhone = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (!d) return "";
  const s = d.startsWith("8") ? "7" + d.slice(1) : d;
  const p = s.padEnd(11, "_").slice(0, 11);
  return `+7 (${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}`.replace(/_/g, "");
};

export default function PublicRegister() {
  const [form, setForm] = useState({ phone: "", name: "", birthday: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/clients/register", {
        phone: form.phone,
        name: form.name,
        birthday: form.birthday || undefined,
      });
      setResult(data);
      if (data.created) toast.success("Готово! Вы зарегистрированы");
      else toast.success("С возвращением, у вас уже есть карта");
    } catch (err) {
      toast.error(formatApiError(err, "Не удалось зарегистрироваться"));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const c = result.client;
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-5 py-10">
        <div className="relative w-full max-w-md">
          <div className="absolute -top-4 -left-4 right-4 bottom-4 border-2 border-burgundy -z-10" />
          <div className="bg-cream-paper border-2 border-burgundy p-8 text-center" data-testid="register-success">
            <Sparkles size={40} className="mx-auto text-gold" strokeWidth={1.5} />
            <p className="mt-4 accent text-burgundy text-2xl">{BRAND.name}</p>
            <h1 className="mt-2 font-heading text-4xl text-ink heading-tight">
              {result.created ? "Добро пожаловать" : "С возвращением"}, <span className="accent text-burgundy">{c.name.split(" ")[0]}</span>
            </h1>
            <p className="mt-4 lede text-ink/85">Ваша карта лояльности активирована.</p>
            <div className="mt-8 border-t-2 border-burgundy/40 pt-6 space-y-4 text-left">
              <div className="flex justify-between items-baseline">
                <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-muted_ink">Баланс</span>
                <span className="font-heading text-4xl text-burgundy">{c.bonus_balance} ₽</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-muted_ink">Уровень</span>
                <span className="font-heading text-2xl text-ink capitalize">{c.tier} · {c.tier_pct}%</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-muted_ink">Телефон</span>
                <span className="font-mono text-ink font-bold">{c.phone}</span>
              </div>
            </div>
            <p className="mt-8 text-sm text-muted_ink italic">
              Покажите телефон на кассе — мы начислим бонусы за покупку и подскажем текущий баланс.
            </p>
            <a href="/" className="btn-primary mt-6 inline-flex" data-testid="register-back-home">На главную</a>
          </div>
        </div>
        <Toaster position="top-right" toastOptions={{ style: { background: "#F4E9D2", color: "#1A0E07", border: "2px solid #4A1419", borderRadius: 0 } }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5 py-10">
      <div className="relative w-full max-w-md">
        <div className="absolute -top-4 -left-4 right-4 bottom-4 border-2 border-burgundy -z-10" />
        <div className="bg-cream-paper border-2 border-burgundy p-8 md:p-10">
          <p className="accent text-burgundy text-xl">{BRAND.name}</p>
          <h1 className="mt-3 font-heading text-4xl md:text-5xl text-ink heading-tight">
            Карта <span className="accent text-burgundy">лояльности</span>
          </h1>
          <p className="mt-4 lede text-ink/85">
            Возвращаем бонусами до 10% с каждой покупки. Подарки на день рождения и праздники.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="register-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.24em] font-bold text-muted_ink mb-2">Ваше имя</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                autoFocus
                placeholder="Как к вам обращаться"
                className="input-vintage"
                data-testid="register-input-name"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.24em] font-bold text-muted_ink mb-2">
                <Phone size={11} strokeWidth={2.5} className="inline mr-1" /> Телефон
              </label>
              <input
                type="tel"
                value={fmtPhone(form.phone)}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
                placeholder="+7 (___) ___-__-__"
                inputMode="numeric"
                className="input-vintage"
                data-testid="register-input-phone"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.24em] font-bold text-muted_ink mb-2">
                День рождения <span className="normal-case text-[10px] text-muted_ink/70">(необязательно — для подарочных бонусов)</span>
              </label>
              <input
                type="date"
                value={form.birthday}
                onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                className="input-vintage"
                data-testid="register-input-birthday"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="register-submit">
              {loading ? "Отправляем…" : "Получить карту"}
            </button>
          </form>

          <p className="mt-6 text-xs text-center text-muted_ink italic">
            Регистрация без пароля — используем ваш номер как идентификатор
          </p>
        </div>
      </div>
      <Toaster position="top-right" toastOptions={{ style: { background: "#F4E9D2", color: "#1A0E07", border: "2px solid #4A1419", borderRadius: 0 } }} />
    </div>
  );
}
